import React, { useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import useStyles from '../../theme/useStyles';
import { useMemberDirectory, useFamilyDirectory, invalidateDirectory } from '../../hooks';
import { formatDate } from '../../utils/format';
import { peakOf, percent } from '../../utils/summaryStats';
import {
  PageHeader, KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge
} from '../../components/ui';
import { congregationSummary } from './analytics';
import type { CongregationMemberLike, CongregationFamilyLike } from './analytics';

export interface CongregationSummaryProps {
  members: CongregationMemberLike[];
  families: CongregationFamilyLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

const FAMILY_STATUS_LABEL: Record<string, string> = { ACTIVE: 'Active', TRANSFERRED: 'Transferred', INACTIVE: 'Inactive' };

/** The Members & Families Summary tab: tiles, then report panels and watch-lists. */
const CongregationSummaryView = ({ members, families, loading, isDarkMode }: CongregationSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => congregationSummary(members, families), [members, families]);
  const alive = s.onRoll + s.guests;
  const lifePeak = peakOf(s.byLifeStatus, (r) => r.count);
  const statusPeak = peakOf(s.byMemberStatus, (r) => r.count);
  const agePeak = peakOf(s.ageBands, (r) => r.count);
  const sizePeak = peakOf(s.householdSizes, (r) => r.count);
  const monthPeak = peakOf(s.joinedMonths, (r) => r.count);
  const yearPeak = peakOf(s.joinedYears, (r) => r.count);
  const baptised = s.sacraments[0];
  const lifeColor = (name: string) => (name === 'Alive' ? t.income : name === 'Deceased' ? t.textFaint : t.warning);
  const lifeName = (name: string) => (name === 'Alive' ? 'Active' : name === 'Deceased' ? 'Passed Away' : name);

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Active Families" value={String(s.activeFamilies)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Members on Roll" value={String(s.onRoll)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Guests" value={String(s.guests)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Avg. Household" value={s.avgHousehold === null ? '—' : String(s.avgHousehold)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Joined This Year" value={String(s.joinedThisYear)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Baptised" value={s.onRoll ? `${baptised.share}%` : '—'} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Inactive" value={String(s.inactive)} color={t.warning} isDarkMode={isDarkMode} />
        <KpiCard label="Passed Away" value={String(s.deceased)} color={t.textFaint} isDarkMode={isDarkMode} />
        <KpiCard label="Transferred Out" value={String(s.transferred)} color={t.warning} isDarkMode={isDarkMode} />
        <KpiCard label="Guest Stays Ending" value={String(s.guestWatch.length)} color={s.guestWatch.length ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Families Without Head" value={String(s.headless.length)} color={s.headless.length ? t.danger : t.income} isDarkMode={isDarkMode} />
      </KpiGrid>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Members by Life Status"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Status', render: (r) => <strong>{lifeName(r.name)}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={lifePeak} color={lifeColor(r.name)} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Members', align: 'right' }
          ]}
          rows={s.byLifeStatus}
          rowKey="name"
          emptyMessage="No members registered yet."
          below={s.membersTotal ? <TotalRow label="Total members" value={s.membersTotal} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Living Members by Category"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Member Status', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={statusPeak} color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Members', align: 'right' }
          ]}
          rows={s.byMemberStatus}
          rowKey="name"
          emptyMessage="No living members yet."
          below={alive ? <TotalRow label="Total living" value={alive} isDarkMode={isDarkMode} /> : null}
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Age Profile"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Age Band', nowrap: true, render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={agePeak} color={t.info} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Members', align: 'right' }
          ]}
          rows={alive ? s.ageBands : []}
          rowKey="name"
          emptyMessage="No living members yet."
        />
        <TablePanel
          title="Gender"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Gender', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.count, alive)} max={100} showPercent color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Members', align: 'right' }
          ]}
          rows={s.genders}
          rowKey="name"
          emptyMessage="No living members yet."
        />
        <TablePanel
          title="Marital Status"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Marital Status', render: (r) => <strong>{r.name}</strong> },
            { key: 'count', header: 'Members', align: 'right' }
          ]}
          rows={s.marital}
          rowKey="name"
          emptyMessage="No living members yet."
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Families by Status"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Status', render: (r) => <strong>{FAMILY_STATUS_LABEL[r.name] || r.name}</strong> },
            { key: 'count', header: 'Families', align: 'right' }
          ]}
          rows={s.familiesByStatus}
          rowKey="name"
          emptyMessage="No families registered yet."
          below={s.familiesTotal ? <TotalRow label="Total families" value={s.familiesTotal} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Household Size (Active Families)"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Living Members', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={sizePeak} color={r.name === 'No living members' ? t.danger : t.info} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Families', align: 'right' }
          ]}
          rows={s.householdSizes}
          rowKey="name"
          emptyMessage="No active families yet."
        />
        <TablePanel
          title="Sacrament Coverage (Roll)"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Sacrament', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={r.share} max={100} showPercent color={t.income} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Members', align: 'right' }
          ]}
          rows={s.onRoll ? s.sacraments : []}
          rowKey="name"
          emptyMessage="No members on the roll yet."
          below={s.onRoll ? <TotalRow label="Members on roll" value={s.onRoll} isDarkMode={isDarkMode} /> : null}
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Joined the Roll by Month"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={monthPeak} color={t.income} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Joined', align: 'right' }
          ]}
          rows={s.joinedMonths}
          rowKey="month"
          emptyMessage="No membership dates recorded yet."
          below={<TotalRow label="Last 12 months" value={s.joinedMonths.reduce((n, m) => n + m.count, 0)} isDarkMode={isDarkMode} />}
        />
        <TablePanel
          title="Joined the Roll by Year"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Year', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={yearPeak} color={t.income} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Joined', align: 'right' }
          ]}
          rows={s.joinedYears}
          rowKey="name"
          emptyMessage="No membership dates recorded yet."
        />
      </SummaryColumns>

      <TablePanel
        title="Guest Stays Ending or Ended"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'name', header: 'Guest', render: (r) => <strong>{r.name || '-'}</strong> },
          { key: 'guestOrigin', header: 'From', wrap: true, render: (r) => r.guestOrigin || '-' },
          { key: 'guestUntil', header: 'Stay Until', nowrap: true, render: (r) => formatDate(r.guestUntil) },
          {
            key: 'days', header: 'Status', nowrap: true,
            render: (r) => (r.days < 0
              ? <Badge tone="error">Ended {Math.abs(r.days)} day{Math.abs(r.days) === 1 ? '' : 's'} ago</Badge>
              : <Badge tone="warning">{r.days === 0 ? 'Ends today' : `${r.days} day${r.days === 1 ? '' : 's'} left`}</Badge>)
          }
        ]}
        rows={s.guestWatch}
        rowKey={(r) => `${r.memberId || r.name}|${r.guestUntil}`}
        emptyMessage="No guest stays end in the next 30 days."
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Active Families Without a Head"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'familyId', header: 'Family', nowrap: true, render: (r) => <strong>{r.familyId || '-'}</strong> },
            { key: 'headOfFamily', header: 'Recorded Head', render: (r) => r.headOfFamily || r.familyName || '-' },
            { key: 'members', header: 'Living Members', align: 'right' }
          ]}
          rows={s.headless}
          rowKey={(r) => String(r.familyId)}
          emptyMessage="Every active family has a head."
        />
        <TablePanel
          title="Members Not Linked to a Family"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Member', render: (r) => <strong>{r.name || '-'}</strong> },
            { key: 'memberId', header: 'ID', nowrap: true, render: (r) => r.memberId || '-' },
            { key: 'familyId', header: 'Family ID', nowrap: true, render: (r) => r.familyId || <Badge tone="neutral">None</Badge> }
          ]}
          rows={s.unlinked}
          rowKey={(r) => `${r.memberId || r.name}`}
          emptyMessage="Every living member belongs to a family."
        />
      </SummaryColumns>
    </>
  );
};

export default CongregationSummaryView;

export interface CongregationSummaryTabProps {
  isDarkMode?: boolean;
  /** The Congregation switch, rendered in place of a title. */
  headerSwitch?: ReactNode;
}

/**
 * The tab as Congregation mounts it. Reads the whole light directory (the
 * member summary projection and the family list — no photos or documents),
 * because every figure is across the whole congregation, not one server page.
 */
export const CongregationSummaryTab = ({ isDarkMode, headerSwitch }: CongregationSummaryTabProps) => {
  const s = useStyles(isDarkMode);
  const { members, loading: membersLoading } = useMemberDirectory();
  const { families, loading: familiesLoading } = useFamilyDirectory();

  // The directory is cached for the session and the Members and Heads tabs do
  // not refresh it after a save, so coming back to the Summary re-reads it —
  // but only when a cached copy was already there, not on the first load.
  const wasCached = useRef(!membersLoading && !familiesLoading);
  useEffect(() => { if (wasCached.current) invalidateDirectory(); }, []);

  return (
    <div style={s.container}>
      <PageHeader leading={headerSwitch} isDarkMode={isDarkMode} />
      <CongregationSummaryView
        members={members}
        families={families}
        loading={membersLoading || familiesLoading}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};
